import React, { useState, useEffect, memo, useContext } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@mui/styles';

import Popper from '@mui/material/Popper';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ToolIcon from '@mui/icons-material/Settings';

import StyledPaper from './styled/paper';
import CopyText from './copytext';

import DataContext from '../data/data-context.js';

const useStyles = makeStyles(theme => ({
    root: {
        overflow: 'hidden',
        whiteSpace: 'inherit', // 'normal' in the detail view and 'nowrap' in the table
        wordBreak: 'break-all',
        textOverflow: 'ellipsis',
    },
    lookup: {
        justifyContent: 'left',
        textAlign: 'left',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        padding: theme.spacing(1),
    },
    title: {
        padding: '8px 24px 8px 12px',
    },
    break: {
        opacity: 0.5,
        paddingLeft: 10,
    },
    popper: {
        zIndex: theme.zIndex.modal,
    },
    gear: {
        pointerEvents: 'all',
        paddingTop: 0,
    },
    titleText: {
        maxWidth: 200,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
}));

function FileHash (props) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [lookups, setLookups] = useState([]);
    const { filehashLookups } = useContext(DataContext);

    const handleOnFileHashTool = e => {
        setAnchorEl(e.currentTarget);
        e.preventDefault();
        e.stopPropagation();
    };

    const handleClose = (e) => {
        setAnchorEl(null);
        e.preventDefault();
        e.stopPropagation();
    };

    const stopPropagation = e => {
        e.stopPropagation();
    };

    useEffect(() => {
        setLookups((filehashLookups || [])
            .map(d => ({ ...d, link: d.url.replace('$file', props.hash) })));
    }, [props.hash, filehashLookups]);

    return (
        <div className={classes.root}>
            <Grid container direction="row" justify="flex-start" alignItems="flex-start" wrap="nowrap">
                <Grid item>
                    {props.hash && <IconButton
                        className={classes.gear}
                        color="primary"
                        aria-label="file-hash-tool"
                        size="small"
                        onClick={handleOnFileHashTool}>
                        <ToolIcon fontSize="inherit" />
                    </IconButton>}
                </Grid>
                <Grid item>
                    {props.hash}
                </Grid>
            </Grid>

            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                className={classes.popper}>
                <ClickAwayListener onClickAway={handleClose}>
                    <StyledPaper className={classes.paper} onClick={stopPropagation}>
                        <div className={classes.title} >
                            <Typography variant="h6">
                                {props.title}
                            </Typography>
                            <CopyText text={props.hash} className={classes.titleText}/>
                        </div>
                        <DialogContent dividers>
                            <Typography variant="subtitle1"> Research </Typography>
                            <div>
                                {lookups.map((m, i) => (
                                    <div key={`${m.name}_${i}`} style={{ paddingTop: 5 }}>
                                        <Link
                                            className={classes.lookup}
                                            aria-label={m.name}
                                            href={m.link}
                                            target="_blank"
                                            rel="noreferrer">
                                            {m.name}
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </DialogContent>
                    </StyledPaper>
                </ClickAwayListener>
            </Popper>
        </div>
    );
}

FileHash.propTypes = {
    title: PropTypes.string,
    hash: PropTypes.string,
};

export default memo(FileHash);
